import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const BAN = {
  hu: { t: "Hozzáférés megtagadva", d: "Az Ön IP-címéről a weboldal elérése le lett tiltva.", reason: "Indok", until: "Lejárat", perm: "Végleges", at: "Tiltás időpontja", by: "Észlelte" },
  en: { t: "Access denied", d: "Access to this website has been blocked for your IP address.", reason: "Reason", until: "Expires", perm: "Permanent", at: "Banned at", by: "Detected by" },
  de: { t: "Zugriff verweigert", d: "Der Zugriff auf diese Website wurde für Ihre IP-Adresse gesperrt.", reason: "Grund", until: "Ablauf", perm: "Dauerhaft", at: "Gesperrt am", by: "Erkannt durch" },
  fr: { t: "Accès refusé", d: "L’accès à ce site a été bloqué pour votre adresse IP.", reason: "Motif", until: "Expiration", perm: "Définitif", at: "Banni le", by: "Détecté par" },
  cs: { t: "Přístup odepřen", d: "Přístup na tento web byl pro vaši IP adresu zablokován.", reason: "Důvod", until: "Platnost do", perm: "Trvale", at: "Zablokováno", by: "Zjištěno" },
};

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZoneName: "short" });

function ServerError() {
  useEffect(() => { document.title = "500 Internal Server Error"; }, []);
  return (
    <div data-testid="server-error-page" style={{ background: "#fff", color: "#000", minHeight: "100vh", fontFamily: "Times New Roman, serif", padding: 8 }}>
      <h1 style={{ textAlign: "center", fontSize: 32, fontWeight: "bold", margin: "21px 0" }}>500 Internal Server Error</h1>
      <hr />
      <p style={{ textAlign: "center", margin: "16px 0" }}>nginx</p>
    </div>
  );
}

function BanNotice({ ban }) {
  const { lang } = useLang();
  const x = BAN[lang] || BAN.en;
  return (
    <div className="grid min-h-screen place-items-center bg-zinc-950 px-5 text-zinc-100" data-testid="ban-notice">
      <div className="w-full max-w-lg rounded-2xl border border-red-500/30 bg-[#111113] p-8">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-red-400">403</div>
        <h1 className="text-3xl font-bold tracking-tight">{x.t}</h1>
        <p className="mt-3 text-[14px] text-zinc-400">{x.d}</p>
        <dl className="mt-6 space-y-3 text-[14px]">
          {ban.reason && <div><dt className="text-[11px] uppercase tracking-wider text-zinc-500">{x.reason}</dt><dd className="mt-0.5 whitespace-pre-wrap text-zinc-200" data-testid="ban-reason">{ban.reason}</dd></div>}
          <div><dt className="text-[11px] uppercase tracking-wider text-zinc-500">{x.until}</dt><dd className="mt-0.5 text-zinc-200" data-testid="ban-expires">{ban.expires_at ? fmt(ban.expires_at) : x.perm}</dd></div>
        </dl>
        <p className="mt-8 border-t border-zinc-800 pt-3 font-mono text-[10px] text-zinc-600" data-testid="ban-meta">
          {x.at}: {ban.created_at ? fmt(ban.created_at) : "—"} · {x.by}: {ban.source || "AutoModDetection"}
        </p>
      </div>
    </div>
  );
}

export function BanGate({ children }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    const onBan = (e) => setState(e.detail);
    window.addEventListener("ip-banned", onBan);
    const recheck = () => api.get("/ban-status").catch((err) => {
      if (err?.response?.status === 500) setState({ banned: true, mode: "silent" });
    });
    const id = api.interceptors.response.use((r) => r, (err) => {
      const s = err?.response?.status;
      const d = err?.response?.data;
      if (s === 403 && d?.banned) window.dispatchEvent(new CustomEvent("ip-banned", { detail: d }));
      else if (s === 500 && !String(err?.config?.url || "").includes("/ban-status")) recheck();
      return Promise.reject(err);
    });
    api.get("/ban-status").then(() => setState((s) => s || { banned: false })).catch((err) => {
      const s = err?.response?.status;
      const d = err?.response?.data;
      setState(d?.banned ? d : s === 500 ? { banned: true, mode: "silent" } : { banned: false });
    });
    return () => { window.removeEventListener("ip-banned", onBan); api.interceptors.response.eject(id); };
  }, []);

  if (!state) return null;
  if (state.banned) return state.mode === "informed" ? <BanNotice ban={state} /> : <ServerError />;
  return children;
}
